import Component from '../core/Component'
import debounce from 'lodash/debounce'
import { substitute } from '../utils/String'

const STATES = {
    OPEN: 'is-open',
    SEARCHING: 'is-searching',
    KEEPOPEN: 'is-keepopen',
}

export const EMITS = {
    CHANGE: 'change',
    SELECT: 'select'
}

export default class Autocomplete extends Component {

    constructor(element, options = {}) {
        super(element, {
            searchEngine: 'AjaxSearch',
            searchEngineOptions: {
                url: null
            },
            ...options
        })

        this.ref = {
            input: null,
            dropdown: null,
            itemTemplate: null,
        }

        this.results = []
        this.highlightedItemEl = null
        this.selectedItem = null
        this.lastValue = ''

        this.isFocus = false
        this.cache = {}

        this.debounceSearchSuggestions = debounce(this.searchSuggestions, 500)
    }

    async prepare() {
        this.ref.input.addEventListener('change', this.handleChange)
        this.ref.input.addEventListener('input', this.handleChange)
        this.ref.input.addEventListener('blur', this.handleInputBlur)
        this.ref.input.addEventListener('focus', this.handleInputFocus)
        this.ref.input.addEventListener('keyup', this.handleKeyUp)
        this.ref.input.addEventListener('keydown', this.handleKeyDown)
        this.ref.dropdown.addEventListener('click', this.handleDropdownClick)
        this.ref.dropdown.addEventListener('mousedown', this.handleDropdownMouseDown)
        window.addEventListener('click', this.handleClickOutside)
        
        //mozna nepoustet pri initu check value, @todo jak ziskat inital value pred tim, nez se pusti js...
        this.checkValue()
    }
    
    destroy() {
        window.removeEventListener('click', this.handleClickOutside)
    }

    handleChange = event => {
        this.checkValue()
    }

    checkValue() {
        const value = this.ref.input.value

        if (this.lastValue === value) {
            return
        }

        this.lastValue = value
        this.emit(EMITS.CHANGE)
        this.debounceSearchSuggestions(value)
    }

    handleKeyDown = event => {
        switch (event.code) {
            case 'Enter':
                if (this.is(STATES.SEARCHING)) {
                    event.preventDefault()
                }
                if (this.highlightedItemEl && this.is(STATES.OPEN)) {
                    event.preventDefault()
                    this.selectItem(this.highlightedItemEl)
                }
                break
            case 'ArrowDown':
            case 'ArrowUp':
                event.preventDefault()
                break
            default:
                this.deselectItem()
                break
        }
    }

    handleKeyUp = event => {
        switch (event.code) {
            case 'Escape':
                this.hide()
                break
            case 'Enter':
            case 'ArrowLeft':
            case 'ArrowRight':
                break
            case 'ArrowDown':
                this.show()
                this.highlightNext()
                break
            case 'ArrowUp':
                this.show()
                this.highlightPrevious()
                break
            default:
                break
        }
    }

    handleClickOutside = (event) => {
        if (!this.element.contains(event.target)) {
            this.element.classList.remove(STATES.KEEPOPEN)
            this.hide()
        }
    }

    handleDropdownMouseDown = () => {
        this.element.classList.add(STATES.KEEPOPEN)
    }
    

    handleDropdownClick = event => {
        const element = event.target.closest('[data-item]')
        if (!element) {
            return
        }
        this.selectItem(element)
    }

    applyResults(results) {
        this.setResults(results)
        if (results.length) {
            this.show()
        } else {
            this.hide()
        }
    }

    setResults(results) {
        this.results = results
        const itemTemplate = this.ref.itemTemplate.innerHTML.trim()

        this.ref.dropdown.innerHTML = results
            .slice(0, 10)
            .map(item => substitute(itemTemplate, item))
            .join('')
    }

    handleInputBlur = () => {
        this.isFocus = false
        this.hide()
    }

    handleInputFocus = () => {
        this.getSearchEngine()

        this.isFocus = true
        this.show()
    }

    async searchSuggestions(value) {
        if (!value) {
            return
        }

        const searchEngine = await this.getSearchEngine()
        if (!searchEngine) {
            return
        }

        searchEngine.abort()
        if (this.cache[value]) {
            this.applyResults(this.cache[value])
        } else {
            const results = await searchEngine.searchSuggestions(value, {

            })
            this.cache[value] = results
            this.results = results
            this.applyResults(results)
        }
    }

    async selectItem(element) {
        this.element.classList.remove(STATES.KEEPOPEN)

        if (!element) {
            return
        }

        const item = element ? JSON.parse(element.dataset.item) : null
        if (!item) {
            return
        }

        this.lastValue = item.title
        this.ref.input.value = item.title
        this.selectedItem = item
        this.hide()
        this.emit(EMITS.SELECT)
    }

    deselectItem() {
        if (!this.selectedItem || (this.selectedItem && this.selectedItem.title === this.ref.input.value)) {
            return
        }
        this.selectedItem = null
    }

    async getValue() {
        let status = 'fail'
        let value = null

        //prazdna hodnota je validni, proto se bere jako vybrana
        if (!this.ref.input.value) {
            status = 'ok'
        }

        //je hodnota vybrana z nabizenych? vratit
        if (this.selectedItem && status !== 'ok') {
            status = 'ok'
            value = this.selectedItem
        }

        //je submitnuto s hodnoutou, ktera nebyla vybrana z nabizenych moznosti? pouzije se searchengine, at najde nejblizsi vhodnou hodnotu
        if (!this.selectedItem && status !== 'ok') {
            try {
                const searchEngine = await this.getSearchEngine()
                const result = await searchEngine.searchQuery(this.ref.input.value)
                if (result) {
                    value = {
                        ...value,
                        ...result
                    }
                }
            } catch (error) {
                console.error(error)
            }
        }

        //je searchengine googlemaps a nejsou znamy souradnice? pak je potreba dotahnout lat/lng a tu na KMBETA i OKRES!
        if (this.options.searchEngine === 'GoogleMapsSearch' && ((value && value.id && value.lat === undefined && value.lng === undefined) || value?.place?.address_components === undefined)) {
            try {
                const searchEngine = await this.getSearchEngine()
                const result = await searchEngine.fetchPlace(value.id)
                if (result) {
                    value = {
                        ...value,
                        ...result
                    }
                }
            } catch (error) {
                console.error(error)
            }
        }

        return value
    }

    show() {
        clearTimeout(this.hideTimer)
        if (this.results.length == 0 || !this.isFocus) {
            return
        }
        
        this.element.classList.add(STATES.OPEN)
    }
    
    hide() {
        if (this.is(STATES.KEEPOPEN)) {
            return
        }
        
        this.hideTimer = setTimeout(() => {
            this.element.classList.remove(STATES.OPEN)
            this.element.classList.remove(STATES.KEEPOPEN)
        }, 500)
    }

    highlightItem(item) {
        this.getItemEls().forEach(itemEl => itemEl.classList.remove('is-active'))
        item?.classList.add('is-active')
        this.highlightedItemEl = item
    }

    highlightPrevious() {
        const itemEls = this.getItemEls()
        if (!itemEls || !this.results) {
            return
        }

        let index = itemEls.findIndex(itemEl => itemEl === this.highlightedItemEl) || 0
        index = index === 0 && itemEls.length - 1 || index - 1

        this.highlightItem(itemEls[index])
    }

    highlightNext() {
        const itemEls = this.getItemEls()
        if (!itemEls) {
            return
        }

        let index = itemEls.findIndex(itemEl => itemEl === this.highlightedItemEl) || 0
        index = (index + 1) % itemEls.length

        this.highlightItem(itemEls[index])
    }

    getItemEls() {
        return [...this.ref.dropdown.querySelectorAll('[data-item]')]
    }

    clearCache() {
        this.cache = {}
    }

    async getSearchEngine() {
        let searchEngine = this._searchEngine

        if (searchEngine) {
            return searchEngine
        }

        try {
            this.element.classList.add(STATES.SEARCHING)
            const module = await import(`../services/SearchEngine/${this.options.searchEngine}`)

            searchEngine = new module.default(this.options.searchEngineOptions)
            searchEngine.onSearchStart = () => this.element.classList.add(STATES.SEARCHING)
            searchEngine.onSearchEnd = () => this.element.classList.remove(STATES.SEARCHING)
            this._searchEngine = searchEngine
        } catch (err) {
            console.error(err)
            console.error(`Failed to import "${this.options.searchEngine}" search engine.`)
        } finally {
            this.element.classList.remove(STATES.SEARCHING)
        }

        return searchEngine
    }
}